// 1. React
import { useEffect, useState } from "react";

// 2. Routing
import { Route, Routes } from "react-router-dom";

// 3. State management
import { useDispatch } from "react-redux";

// 4. Helpers / Auth
import { classNames } from "helpers";
import { getAccessToken } from "services/auth";

// 5. Redux actions
import { fetchActiveUser } from "services/api/users/redux";
import { fetchTranslations } from "services/api/translations/redux";
import { fetchLanguages } from "services/api/languages/redux";

// 6. Layout components
import { Menu } from "../Menu";
import Sidebar from "../Sidebar/Sidebar";

// 7. App navigation / types
import { Routes as AppRoutes } from "../../navigation";
import { AppHomeCmp } from "./types";

// 8. Moduli
import { DashboardRouter } from "ui/modules/dashboard/navigation";
import { TenantRouter } from "ui/modules/tenants/navigation";
import { PricingRouter } from "ui/modules/pricing/navigation";
import { TranslationRouter } from "ui/modules/translations/navigation";
import { CronJobRouter } from "ui/modules/cronjobs/navigation";
import { SettingRouter } from "ui/modules/settings/navigation";
import AccountPage from "ui/modules/account/pages/AccountPage/AccountPage";

const SIDEBAR_STORAGE_KEY = "openSidebar";

/**
 * Ljuska administratorske aplikacije. Za razliku od korisničke, ovdje nema `FleetGuard`-a ni
 * postavki tenanta — admin ne radi ni s jednom flotom, nego s platformom.
 */
const AppHome: AppHomeCmp = (_props: any) => {
  const dispatch = useDispatch();
  const [menuHeight, setMenuHeight] = useState(0);

  const [openSidebar, setOpenSidebar] = useState<boolean>(() => {
    const saved = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    const accessToken = getAccessToken();

    if (accessToken) {
      dispatch(fetchActiveUser() as any);
      dispatch(fetchTranslations() as any);
      dispatch(fetchLanguages() as any);
    }

    calculateMenuHeight();
  }, [dispatch]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(openSidebar));

    if (openSidebar) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [openSidebar]);

  const calculateMenuHeight = () => {
    const menu = document.getElementById("js-menu-top");
    if (menu) setMenuHeight(menu.offsetHeight);
  };

  const handleSidebarToggle = () => {
    setOpenSidebar((prev) => !prev);
  };

  return (
    <>
      <Menu openMenu={openSidebar} onOpenMenu={handleSidebarToggle} />

      <div
        className="flex bg-[#161719]"
        style={{ height: `calc(100vh - ${menuHeight}px)` }}
      >
        <Sidebar openSidebar={openSidebar} onOpenSidebar={handleSidebarToggle} />

        <div
          className={classNames(
            "w-full bg-[#f9fafc] rounded mr-0 xl:mr-3 mb-[-1px] xl:mb-3 overflow-y-auto"
          )}
        >
          {/*
            Svaki modul ima svoj `<Routes>` blok — kao u korisnickoj aplikaciji. Blokovi se
            poklapaju neovisno, pa vise rutera moze dijeliti istu „*" rutu.
          */}
          <Routes>
            <Route path={DashboardRouter.route} element={<DashboardRouter />} />
          </Routes>
          <Routes>
            <Route path={TenantRouter.route} element={<TenantRouter />} />
          </Routes>
          <Routes>
            <Route path={PricingRouter.route} element={<PricingRouter />} />
          </Routes>
          <Routes>
            <Route path={TranslationRouter.route} element={<TranslationRouter />} />
          </Routes>
          <Routes>
            <Route path={CronJobRouter.route} element={<CronJobRouter />} />
          </Routes>
          <Routes>
            <Route path={SettingRouter.route} element={<SettingRouter />} />
          </Routes>
          <Routes>
            <Route path="/account" element={<AccountPage />} />
          </Routes>
        </div>
      </div>
    </>
  );
};

AppHome.route = AppRoutes.AppHome;

export default AppHome;
